import React, { useEffect, useMemo, useState } from 'react';

import './ResetPassword.scss';

import { AuthFormLayout } from 'layouts/AuthFormLayout/AuthFormLayout';
import { useNavigate } from 'react-router-dom';
import { routes } from 'enums';
import { CustomInput } from 'components/CustomInput';
import { Navigate } from 'react-router-dom';
import { useActions, useAppDispatch, useAppSelector } from 'hooks';
import {
  accessTokenSelect,
  authActions,
  authThunk,
  errorSelect,
  resetExpiresAtSelect,
  resetPhoneSelect,
  resetResendAvailableAtSelect,
  resetTokenSelect,
} from 'state';

/** Six digits, matching the generator and the mobile app's six-cell field. */
const CODE_LENGTH = 6;

/** How often the countdowns are recomputed from their absolute deadline. */
const TICK_MS = 250;

/**
 * Seconds left until an absolute instant, recomputed rather than decremented.
 *
 * A counter that subtracts one per second drifts, stops while the tab is
 * backgrounded, and restarts from the top on a remount. Every one of those ends
 * with a Resend link offered before the server will honour it, and a 429 the
 * admin cannot explain. Deriving from the deadline on every tick cannot drift,
 * and a backgrounded tab returns showing the truth.
 */
const useSecondsUntil = (instant?: string): number => {
  const deadline = useMemo(
    () => (instant ? new Date(instant).getTime() : 0),
    [instant],
  );

  const remaining = () =>
    deadline ? Math.max(0, Math.ceil((deadline - Date.now()) / 1000)) : 0;

  const [seconds, setSeconds] = useState(remaining);

  useEffect(() => {
    setSeconds(remaining());

    if (!deadline) return undefined;

    const id = setInterval(() => setSeconds(remaining()), TICK_MS);

    return () => clearInterval(id);
  }, [deadline]);

  return seconds;
};

/** m:ss. Padded on the seconds only, so 70 reads "1:10" and 9 reads "0:09". */
export const formatCountdown = (totalSeconds: number): string => {
  const safe = Math.max(0, Math.floor(totalSeconds));

  return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, '0')}`;
};

/**
 * Step 2 of 3 of an administrator's password reset: prove the SMS code.
 *
 * ## Why this screen is the whole point
 *
 * Between 2026-07-29 and now this step did not exist. `restore-password` took a
 * phone number and confirmed the reset in the same request, so the flow went
 * straight from "who are you" to "choose a new password" -- and knowing an
 * administrator's number was the entire proof required to take over the account
 * with the most privilege in the product.
 *
 * So the assertion worth making about this page is not that it works, it is
 * that it is REACHED. `Login` navigates here rather than to /change-password,
 * and /change-password refuses to render until `resetVerified` is set, which
 * only happens when the server has accepted a code.
 *
 * ## The token changes underneath this screen
 *
 * Submitting exchanges the step-1 token for a `reset_commit` token with a fresh
 * nonce -- the slice swaps it -- and burns the old one. That is why leaving and
 * coming back needs a new code rather than a re-submit: the thing that got you
 * here is spent the moment it works.
 */
export const ResetPassword = () => {
  const [code, setCode] = useState('');
  const navigate = useNavigate();
  const token = useAppSelector(accessTokenSelect);
  const error = useAppSelector(errorSelect);
  const dispatch = useAppDispatch();
  const { restorePassword } = useActions(authThunk);
  const { verifyReset } = useActions(authThunk);
  const resetToken: string = useAppSelector(resetTokenSelect);
  const resetPhone: string = useAppSelector(resetPhoneSelect);
  const expiresAt: string = useAppSelector(resetExpiresAtSelect);
  const resendAvailableAt: string = useAppSelector(resetResendAvailableAtSelect);

  const expiresIn = useSecondsUntil(expiresAt);
  const resendIn = useSecondsUntil(resendAvailableAt);

  const hasExpired = Boolean(expiresAt) && expiresIn === 0;
  const cooldownRunning = resendIn > 0;

  const handleSubmit = async () => {
    const trimmed = code.trim();

    // Checked here rather than sent: the field is typed by hand, and the server
    // answers a malformed code with a field-level validation error that says
    // nothing useful to the person looking at one input box.
    if (!new RegExp(`^\\d{${CODE_LENGTH}}$`).test(trimmed)) {
      dispatch(
        authActions.setError(
          `Enter the ${CODE_LENGTH}-digit code from the SMS.`,
        ),
      );

      return;
    }

    await verifyReset({
      verifyData: { token: resetToken, code: +trimmed },
      navigate,
    });
  };

  useEffect(() => {
    if (token) {
      navigate(routes.MAIN.INIT);
    }
  }, [token]);

  /**
   * Ask for another code.
   *
   * Inert while the cooldown runs, because the server enforces sixty seconds
   * between codes and answers an early request with OTP_RESEND_TOO_SOON. A link
   * that looks live and returns an error teaches an admin to distrust the page.
   *
   * The new code REPLACES the old one server-side, so the field is cleared:
   * leaving the previous digits there invites submitting a code that is now
   * guaranteed to fail.
   */
  const handleResendCode = async () => {
    if (!resetPhone || cooldownRunning) return;

    setCode('');
    dispatch(authActions.clearError());
    await restorePassword({ restoreData: { phone: resetPhone } });
  };

  // Reaching this page means restore-password succeeded and a code is in
  // flight. Arriving without a token -- by typing the URL, or after a refresh,
  // which clears the store -- leaves nothing to verify, so start over.
  if (!resetToken) {
    return <Navigate to={routes.AUTH.LOG_IN} replace />;
  }

  return (
    <AuthFormLayout
      title={'Reset password'}
      // Said "email"; the code is sent by SMS to the phone number entered on
      // the login screen, and there is no email anywhere in this flow.
      subtitle={
        resetPhone
          ? `Enter the ${CODE_LENGTH}-digit code we sent to ${resetPhone}`
          : `Enter the ${CODE_LENGTH}-digit code we sent to your phone`
      }
      onSubmit={handleSubmit}
      handleResendResetClick={handleResendCode}
      bottomText={
        cooldownRunning
          ? `Resend code in ${formatCountdown(resendIn)}`
          : 'Resend code'
      }
      isArrow
    >
      <div style={{ width: '70%', margin: '0 auto' }} data-testid="reset-code-field">
        <CustomInput
          error={error}
          clearError={() => dispatch(authActions.clearError())}
          title={'Confirmation code'}
          value={code}
          onChange={setCode}
        />
      </div>

      {/*
        The code's own deadline, separate from the resend cooldown.

        Worth showing because the two errors it prevents send an admin to
        opposite actions: a code that has expired needs a new one, while a code
        that was mistyped needs retyping. Without this the screen looks the same
        either way and the server's answer arrives after the attempt.
      */}
      <div
        data-testid="reset-code-timer"
        style={{
          minHeight: 22,
          fontSize: 14,
          textAlign: 'center',
          margin: '0 auto',
          color: hasExpired ? 'red' : '#666',
        }}
      >
        {hasExpired
          ? 'That code has expired. Choose Resend code for a new one.'
          : expiresAt
            ? `Code expires in ${formatCountdown(expiresIn)}`
            : ''}
      </div>

      {/*
        minHeight rather than a fixed height, and role="alert" so the failure is
        announced -- the same fix as the login form next door, where a fixed
        26px clipped every message longer than "Invalid credentials".
      */}
      <div
        role="alert"
        data-testid="reset-code-error"
        style={{
          minHeight: 26,
          fontSize: 15,
          color: 'red',
          textAlign: 'center',
          maxWidth: '70%',
          margin: '0 auto',
        }}
      >
        {error}
      </div>
    </AuthFormLayout>
  );
};
